/* eslint-disable max-len */
import COS from '@/plugins/cos_sdk/src/cos';
import eventProxy from '@/scripts/tools/eventProxy';
import user from '@/scripts/helper/user';
import { queryParamsToString } from '@/scripts/tools/utils';
import { getRequestInfo } from '@/scripts/tools/cosSDKUtils';
import { store } from '@/scripts/store';
import storage, { Storage } from '@/scripts/tools/storage';

const cos = new COS({
  // 可选参数
  FileParallelLimit: 2, // 控制文件上传并发数
  ChunkParallelLimit: 2, // 控制单个文件下分片上传并发数
  ChunkSize: 1024 * 1024, // 控制分片大小，单位 B
  ProgressInterval: 1000, // 控制 onProgress 回调的间隔
  SliceSize: 8 * 1024 * 1024,

  UploadCheckContentMd5: true,
  CopyChunkParallelLimit: 1, // 复制文件的分块并发数
  UserAgent: window.navigator.userAgent,
  getAuthorization(options, callback) {
    user.getCredential().then(credential => {
      const SecretId = credential.SecretId || credential.tmpSecretId;
      const SecretKey = credential.SecretKey || credential.tmpSecretKey;
      options = { ...options, SecretId, SecretKey };
      const res = { Authorization: COS.getAuthorization(options) };
      if (credential.token) {
        res.SecurityToken = credential.token;
      }
      callback(res);
    });
  },
});

// 可配置的字段
[
  'UploadDoubleCheck',
].forEach(key => {
  cos.options[key] = storage.get(`upload.${key}`);
  Storage.onChange(`upload.${key}`, val => {
    cos.options[key] = val;
  });
});

cos.on('list-update', data => {
  eventProxy.emit('upload-list-update', data);
});

// access-rules 中间件下发的拒绝标记。COS SDK 走自己的 HTTP 实现（/cosproxy），
// 不经过 dao.js 的 axios 拦截器，必须在这里单独捕获，统一交由 App.vue 处理（登出 + 提示）
const ACCESS_BLOCKED_ERR_CODES = new Set(['ACCESS_BLOCKED', 'LONGTERM_KEY_DISABLED']);
// OAuth binding 校验失败标记。后端 validate-oauth-binding 中间件下发，
// 上线瞬间在线 OAuth 用户的旧 STS 没 binding 凭证时会大范围出现。
// 统一交由 App.vue 处理：当作登录态过期 → 登出 + 跳登录页 + 提示重登（见下面 emit / App.vue 监听）
const OAUTH_BINDING_INVALID = 'OAUTH_BINDING_INVALID';
// AK 白名单登录 ticket cookie 缺失/失效标记。后端 access-rules 中间件下发。
// 场景：AK 白名单客户关闭浏览器（会话级 cookie 失效）后 tab 里的 SDK 请求仍在飞，就会命中。
// 统一交由 App.vue 处理：当作登录态过期 → 登出 + 跳登录页 + 提示重登
const AK_HANDSHAKE_REQUIRED = 'AK_HANDSHAKE_REQUIRED';
cos.on('after-receive', receive => {
  // /cosproxy 命中 access-rules 时，server 返回 JSON：
  //   { code: -1, errCode: 'ACCESS_BLOCKED', message, blockedAuth, currentAuth }
  // 正常 COS XML 响应不会有 errCode 字段，安全可识别
  // 整段包 try/catch，任何异常（解析、emit 下游 handler 抛错等）都不能影响 SDK 主流程
  try {
    const body = receive && receive.body;
    if (!body) return;
    let payload = null;
    if (typeof body === 'string') {
      // SDK 透传的原始 body 是字符串。仅当看起来像 JSON 时才 parse，避免误吃 XML
      if (body.charAt(0) !== '{') return;
      try {
        payload = JSON.parse(body);
      } catch (e) {
        return;
      }
    } else if (typeof body === 'object') {
      payload = body;
    } else {
      return;
    }
    if (!payload) return;
    if (ACCESS_BLOCKED_ERR_CODES.has(payload.errCode)) {
      eventProxy.emit('access-blocked', {
        errCode: payload.errCode,
        message: payload.message,
        blockedAuth: payload.blockedAuth,
        currentAuth: payload.currentAuth,
      });
      return;
    }
    if (payload.errCode === OAUTH_BINDING_INVALID) {
      // 当次请求的失败保留原错误链路（不 swallow），统一交 App.vue 处理：登出 + 提示重登
      eventProxy.emit('oauth-binding-invalid', { errCode: payload.errCode });
    }
    if (payload.errCode === AK_HANDSHAKE_REQUIRED) {
      // 同上，AK 白名单场景 cookie 失效时走这个分支
      eventProxy.emit('ak-handshake-required', { errCode: payload.errCode });
    }
  } catch (e) {
    // 兜底：access-rules 拦截只是附加能力，绝不能影响 SDK 正常请求闭环
    console.warn('[access-rules] after-receive handler error', e);
  }
});

cos.on('before-send', opt => {
  const res = getRequestInfo(opt);

  if (res.action === 'PutObject' || res.action === 'UploadPart') {
    // opt.qs['x-cos-channel-id'] = 'cosbrowser_web';
    // 流量大的接口直接走跨域请求
    return;
  } if (res.action === 'GetService') {
    return;
  }

  if (opt.qs) {
    const qsStr = queryParamsToString(opt.qs);
    if (qsStr) {
      opt.url += (opt.url.indexOf('?') > -1 ? '&' : '?') + qsStr;
    }
    delete opt.qs;
  }

  // 避免 url 里的特殊字符被转义
  opt.url = opt.url.replace(/^(https?:\/\/(.*)\.(cos|ci|pic)\.([^.]+)\.myqcloud\.com)\/(.*)$/, (o, host, bucket, hostType, region, pathQuery) => `${store.cosbrowserServerUrl}/cosproxy/?bucket=${bucket}&region=${region}&hostType=${hostType}&params=${encodeURIComponent(pathQuery)}`);

  // URL 改写完成后，对走 /cosproxy 代理的请求注入 OAuth binding header。
  // 接入层 validate-oauth-binding 中间件需要该 header 才允许 OAuth 临时密钥请求通过。
  //
  // 关键设计：从 opt.headers.Authorization 里反解出 SDK 本次签名实际使用的 SecretId，
  // 再按 SecretId 维度从 user.getBindingBySecretId 拿对应 sig，**确保签名与 binding
  // 校验来自同一份续期产物**。避免并发续期场景下 credential 全局态被覆盖造成的
  // sig/secretId 错位 → 服务端 SIG_MISMATCH 403。
  //
  // 兼容性原则：
  //  - 只在 URL 已被改写为代理路径时注入（外部 COS 直连不走 cosbrowser-server）
  //  - 永久密钥 / 未命中 map（兼容期）→ 静默跳过
  //  - try/catch 兜底，绝不阻塞 SDK 主流程
  try {
    if (opt.url && opt.url.indexOf(store.cosbrowserServerUrl) === 0) {
      const auth = (opt.headers && opt.headers.Authorization) || '';
      // Authorization 形如 q-sign-algorithm=sha1&q-ak=AKID...&q-sign-time=...
      const ak = (auth.split('&').find(seg => seg.indexOf('q-ak=') === 0) || '').slice(5);
      const binding = ak && user.getBindingBySecretId && user.getBindingBySecretId(ak);
      if (binding && binding.sig && binding.expireAt) {
        opt.headers = opt.headers || {};
        opt.headers['x-cos-binding-sig'] = binding.sig;
        opt.headers['x-cos-binding-expire'] = String(binding.expireAt);
      }
    }
  } catch (e) {
    console.warn('[binding header inject] cos before-send error', e);
  }
});

// eventProxy.on('after-logout', () => {
//   cos.removeAllTask();
// });

// 基于cos.getObjectUrl修改，设置桶里面的文件和文件夹的下载url
cos.getObjectUrlByDomain = (params, callback) => {
  // 拿到当前桶的下载域名，如果不是默认的域名，那么就是自定义域名，只能使用http协议，并且需要将原本默认域名替换,且不支持后缀式
  let bucketDownloadDomain = '';
  if (store.downloadDomain[params.Bucket]) {
    bucketDownloadDomain = store.downloadDomain[params.Bucket];
  } else {
    bucketDownloadDomain = storage.get(`downloadDomain.${params.Bucket}`);
  }
  let protocol = params.Protocol || cos.options.Protocol;
  let domain = cos.options.Domain;
  let { ForcePathStyle } = cos.options;
  if (bucketDownloadDomain !== 'cosDefaultDomain' && bucketDownloadDomain !== '') {
    protocol = 'http:';
    domain = bucketDownloadDomain;
    ForcePathStyle = false;
  }
  params.protocol = protocol;
  params.domain = domain;
  params.ForcePathStyle = ForcePathStyle;
  const url = cos.getObjectUrl(params, callback);
  return url;
};

// 生成存储桶访问域名
cos.getDefaultBucketDomain = params => {
  let url = cos.getObjectUrl({
    Bucket: params.Bucket,
    Region: params.Region,
    Key: '/',
    Sign: false,
  });
  url = (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return url;
};

// 获取文件下载链接
/**
 *
 * @param {*} url 对象的公有链接
 * @param {*} unSigned 是否签名: true-不签名 false-签名
 * @param {*} expires 有效时间
 * @param {*} method 方法
 * @param {*} force 是否强制获取一边密钥（oauth 需要）
 */
cos.getDownloadUrl = async function getDownloadUrl(url, unSigned, expires, method, force) {
  url = url.replace(/\?.*$/, '');
  const hostSign = url.replace(/^https?:\/\/(.*)\.cos\.([^.]+)\.myqcloud\.com\/(.*)$/, '$1.cos.$2.myqcloud.com');
  const key = decodeURIComponent(url.replace(/^\w+:\/\/[^/]+/, '')) || '/';
  if (unSigned) return url;
  const credential = await user.getCredential(undefined, force);
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const authOpt = {
    SecretId,
    SecretKey,
    Method: method || 'GET',
    Key: key,
    Expires: parseInt(expires, 10) || 1800,
    SystemClockOffset: this.options.SystemClockOffset,
    Headers: {
      host: hostSign,
    },
  };
  let result = `${url}?${cos.getAuth(authOpt)}`;
  if (credential.token) {
    result += `&x-cos-security-token=${credential.token}`;
  }
  return result;
};

// 获取完整的文件列表
cos.listFiles = function listFiles(params, callback) {
  const Contents = [];
  let marker;
  const next = function next() {
    params.Marker = marker;
    cos.getBucket(params, (err, data) => {
      if (err) {
        err.errorAction = 'getBucket';
        callback(err);
      } else {
        if (data && data.Contents && data.Contents.length) {
          Contents.push(...data.Contents);
        }
        if (data.IsTruncated === 'true') {
          marker = data.NextMarker;
          next();
        } else {
          callback(null, { Contents });
        }
      }
    });
  };
  next();
};

// 覆盖检查任务状态的方法，协助文件同步的任务状态判断
const syncUploading = {};
cos.__isRunningTask = cos._isRunningTask;
cos._isRunningTask = id => syncUploading[id] || cos.__isRunningTask(id);
cos.setSyncUploading = id => {
  syncUploading[id] = true;
};
cos.removeSyncUploading = id => {
  delete syncUploading[id];
};
cos.removeTask = cos.cancelTask;

export default cos;
